import { createContext, useContext, ReactNode, useEffect } from 'react';
import {
  useExtendedThemeSwitcher,
  type BuiltInTheme,
  type UseExtendedThemeSwitcherReturn
} from '../reactvideoeditor/pro/hooks/use-extended-theme-switcher';

interface ThemeContextType {
  currentTheme: BuiltInTheme;
  setTheme: (theme: BuiltInTheme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * ThemeProvider component that manages light/dark theme with system preference detection
 *
 * Features:
 * - Detects system preference on first visit (when no saved preference exists)
 * - Persists user's manual theme choice in localStorage
 * - Manual choice takes precedence over system preference
 * - Listens for system preference changes (only applies if user hasn't set a preference)
 */
export function ThemeProvider({ children }: ThemeProviderProps) {
  // Helper to safely access localStorage
  const safeGetLocalStorage = (key: string): string | null => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  };

  // Detect system preference
  const getSystemPreference = (): BuiltInTheme => {
    if (typeof window === 'undefined') return 'light';

    try {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      return mediaQuery.matches ? 'dark' : 'light';
    } catch {
      return 'light';
    }
  };

  // Determine initial theme: saved preference or system preference
  const getInitialTheme = (): BuiltInTheme => {
    const savedTheme = safeGetLocalStorage('rve-extended-theme');

    if (savedTheme === 'light' || savedTheme === 'dark') {
      return savedTheme;
    }

    // No saved preference, use system preference
    return getSystemPreference();
  };

  // Initialize theme switcher with detected initial theme
  const { currentTheme, setTheme: setExtendedTheme } = useExtendedThemeSwitcher({
    showDefaultThemes: true,
    defaultTheme: getInitialTheme(),
    customThemes: []
  });

  // Listen for system preference changes
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if user hasn't manually set a preference
      const hasManualPreference = safeGetLocalStorage('rve-extended-theme');

      if (!hasManualPreference) {
        const newTheme: BuiltInTheme = e.matches ? 'dark' : 'light';
        setExtendedTheme(newTheme);
      }
    };

    // Modern browsers
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [setExtendedTheme]);

  // Type-safe theme setter (only allows light/dark)
  const setTheme = (theme: BuiltInTheme) => {
    setExtendedTheme(theme);
  };

  const value: ThemeContextType = {
    currentTheme: currentTheme as BuiltInTheme,
    setTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook to access theme context
 * Must be used within ThemeProvider
 */
export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
}
